import { useState, useRef, useEffect } from "react";
import { useAuth, API, useAngelaPage } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Sparkles, Send, X, Image, Target, Palette, Eye, FileText,
  Hammer, ClipboardList, Home, Users, Settings, LayoutDashboard, Lightbulb
} from "lucide-react";

// Per-page quick actions and greetings
const PAGE_CONFIG = {
  // Project stages
  lead: {
    greeting: "I can see you're working on a new lead. Let me help you qualify it and capture the right info.",
    actions: [
      { label: "Draft client vision", action: "Based on the project type and property details, help me draft a compelling 'In Their Own Words' vision statement that captures what the client wants" },
      { label: "Suggest must-haves", action: "Based on this project type and scope, suggest a list of must-have features the client should consider" },
      { label: "Identify deal-breakers", action: "Help me identify common deal-breakers and hard constraints for this type of project that I should discuss with the client" },
    ],
    icon: Home,
  },
  opportunity: {
    greeting: "Let's build a complete picture of this opportunity. I can help fill in the details and score it.",
    actions: [
      { label: "Fill missing fields", action: "Review the opportunity data and suggest what's missing. Help me fill in the gaps to improve the qualification score" },
      { label: "Draft scope summary", action: "Based on the opportunity data entered so far, write a professional project scope summary" },
      { label: "Suggest next steps", action: "Analyze this opportunity and suggest the best next steps to move it forward" },
    ],
    icon: Target,
  },
  concept: {
    greeting: "I'm ready to help with your concept. I can generate AI renderings, refine prompts, or help present designs to your client.",
    actions: [
      { label: "Generate concepts", action: "generate_concepts", apiAction: "generate_concepts" },
      { label: "Refine my prompt", action: "Help me write a better, more detailed prompt for AI concept generation that captures the design vision" },
      { label: "Compare concepts", action: "Compare the generated concepts and recommend which one best matches the client's vision" },
    ],
    icon: Palette,
  },
  dream: {
    greeting: "I can help analyze reference photos and articulate the client's design vision.",
    actions: [
      { label: "Analyze these photos", action: "Look at the reference images uploaded and describe what you see — materials, style, condition, and potential" },
      { label: "Suggest design directions", action: "Based on the project type and style preferences, suggest 3 design directions we could explore" },
      { label: "Write design brief", action: "Help me write a design brief that captures the client's vision, must-haves, and inspiration" },
    ],
    icon: Palette,
  },
  visualize: {
    greeting: "I'm ready to generate and refine concepts. Describe your vision or ask me to iterate.",
    actions: [
      { label: "Refine my prompt", action: "Help me write a better prompt for AI concept generation. I want it to be detailed and specific" },
      { label: "Compare concepts", action: "Compare the generated concepts and recommend which one best matches the client's vision" },
      { label: "Draft client message", action: "Help me write a message to the client about these concept renderings" },
    ],
    icon: Eye,
  },
  specify: {
    greeting: "I can help build your quote — suggest line items, review pricing, and draft terms.",
    actions: [
      { label: "Suggest line items", action: "Based on the project scope and approved concept, suggest line items with estimated quantities and pricing" },
      { label: "Review pricing", action: "Review the current line items and pricing. Flag anything that seems off or missing" },
      { label: "Draft cover letter", action: "Write a professional cover letter to accompany this quote to the client" },
    ],
    icon: FileText,
  },
  plans: {
    greeting: "I can help organize your project plans and documentation.",
    actions: [
      { label: "Create plan checklist", action: "Generate a checklist of all plans and documents needed for this project" },
      { label: "Summarize specifications", action: "Summarize the project specifications into a clear, organized format" },
      { label: "Flag permit requirements", action: "Based on the project details, identify what permits or approvals might be needed" },
    ],
    icon: FileText,
  },
  build: {
    greeting: "I can help manage the build — suggest tasks, flag risks, and track progress.",
    actions: [
      { label: "Suggest tasks", action: "Based on the quote line items, suggest detailed build tasks with logical ordering" },
      { label: "Flag risks", action: "Review the project details and flag any potential risks, delays, or issues to watch for" },
      { label: "Draft progress update", action: "Help me write a progress update to send to the client about the build status" },
    ],
    icon: Hammer,
  },
  complete: {
    greeting: "Congrats on completing the project! I can help with closeout documentation.",
    actions: [
      { label: "Generate punch list", action: "Generate a final punch list of items to inspect and verify before project closeout" },
      { label: "Draft completion letter", action: "Write a project completion letter to send to the client" },
      { label: "Request testimonial", action: "Draft a friendly message asking the client for a testimonial or review" },
    ],
    icon: ClipboardList,
  },
  // Non-project pages
  dashboard: {
    greeting: "I can help you understand your pipeline and prioritize your day.",
    actions: [
      { label: "Summarize pipeline", action: "Give me an overview of my current project pipeline — how many at each stage" },
      { label: "What needs attention?", action: "Which projects need my attention today? Any overdue items or upcoming deadlines?" },
      { label: "Weekly summary", action: "Generate a weekly summary of project progress and key metrics" },
    ],
    icon: LayoutDashboard,
  },
  projects: {
    greeting: "I can help you manage your projects. What would you like to do?",
    actions: [
      { label: "Help create a project", action: "Help me create a new project with a compelling description" },
      { label: "Pipeline overview", action: "Summarize my project pipeline and highlight which ones need attention" },
      { label: "Sort by priority", action: "Help me prioritize my current projects based on value, urgency, and stage" },
    ],
    icon: FileText,
  },
  settings: {
    greeting: "I can help you configure your account and quote defaults.",
    actions: [
      { label: "Review my settings", action: "Review my current settings and suggest any improvements" },
      { label: "Draft payment terms", action: "Help me write professional default payment terms for my quotes" },
      { label: "Draft quote notes", action: "Write default additional notes that should appear on all my quotes" },
    ],
    icon: Settings,
  },
  crew: {
    greeting: "I can help you manage your crew and assignments.",
    actions: [
      { label: "Suggest crew assignments", action: "Based on the project requirements, suggest which crew members to assign" },
      { label: "Draft crew brief", action: "Write a project brief to share with the crew for this job" },
    ],
    icon: Users,
  },
  general: {
    greeting: "Hi! I'm Angela, your AI assistant. I can help with projects, quotes, designs, and more.",
    actions: [
      { label: "What can you do?", action: "Tell me everything you can help me with in Arq+" },
      { label: "Help with a project", action: "Help me with one of my active projects" },
      { label: "Estimate costs", action: "Help me estimate costs for a construction project" },
    ],
    icon: Sparkles,
  },
};

export default function AngelaChat({ isOpen, onToggle }) {
  const { token } = useAuth();
  const { page, stage, projectId, projectData } = useAngelaPage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastContextKey, setLastContextKey] = useState(null);
  const [insights, setInsights] = useState([]);
  const messagesEndRef = useRef(null);

  // Determine active context key
  const contextKey = page === "project" && stage ? stage : page || "general";
  const config = PAGE_CONFIG[contextKey] || PAGE_CONFIG.general;

  // Load learned patterns (pre-mined; no chat-time LLM cost) when the panel opens.
  useEffect(() => {
    if (!isOpen || !token) return;
    axios
      .get(`${API}/angela/insights`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setInsights(r.data?.insights || []))
      .catch(() => setInsights([]));
  }, [isOpen, token]);

  const dismissInsight = async (id) => {
    setInsights((prev) => prev.filter((i) => i.id !== id));
    try {
      await axios.put(
        `${API}/angela/insights/${id}`,
        { status: "dismissed" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      /* optimistic — leave removed even if the call fails */
    }
  };

  // Reset messages when context changes
  useEffect(() => {
    if (isOpen && contextKey !== lastContextKey) {
      setLastContextKey(contextKey);
      setMessages([{
        role: "angela",
        content: config.greeting,
        timestamp: new Date().toISOString(),
        showActions: true,
      }]);
    }
  }, [isOpen, contextKey]);

  // Show welcome on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "angela",
        content: config.greeting,
        timestamp: new Date().toISOString(),
        showActions: true,
      }]);
      setLastContextKey(contextKey);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleApiAction = async (actionType) => {
    if (actionType === "generate_concepts") {
      const userMsg = { role: "user", content: "Generate concepts for this project", timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, userMsg]);
      setLoading(true);

      try {
        // First ask Angela for a good prompt via chat
        const chatRes = await axios.post(`${API}/angela/chat`, {
          message: `I need you to generate AI concept images for this project. Based on the project vision and details, create the concepts now. If there's a description or vision already set, use that as the basis.`,
          project_id: projectId,
          context_type: "concept",
          project_title: projectData?.title || null,
          project_stage: stage || null,
        }, { headers: { Authorization: `Bearer ${token}` } });

        // Now trigger the actual FLUX generation
        const description = projectData?.description || projectData?.title || "Modern construction design concept";
        await axios.post(`${API}/angela/generate-concepts`, null, {
          params: { project_id: projectId, description },
          headers: { Authorization: `Bearer ${token}` },
        });

        setMessages(prev => [...prev, {
          role: "angela",
          content: `I'm generating AI concept renderings now. This typically takes 30-60 seconds.\n\n**Prompt used:** "${description}"\n\nHead over to the **Concepts** tab to see them appear. I'll create multiple design options for you to review.`,
          suggestions: ["Check concept status", "Refine my prompt", "Help with something else"],
          timestamp: new Date().toISOString(),
        }]);
      } catch (err) {
        const detail = err.response?.data?.detail || "Failed to generate concepts";
        setMessages(prev => [...prev, {
          role: "angela",
          content: `I had trouble generating concepts: ${detail}. You can try again or refine the project description first.`,
          suggestions: ["Try again", "Help me write a better description"],
          timestamp: new Date().toISOString(),
        }]);
      } finally { setLoading(false); }
    }
  };

  const sendMessage = async (text = input) => {
    if (!text.trim()) return;
    const userMessage = { role: "user", content: text, timestamp: new Date().toISOString() };
    // Prior turns (before this new message) for conversational continuity.
    const history = messages
      .filter((m) => m.role === "user" || m.role === "angela")
      .map((m) => ({ role: m.role === "angela" ? "assistant" : "user", content: m.content }));
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/angela/ask`, {
        message: text,
        project_id: projectId || null,
        history,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setMessages(prev => [...prev, {
        role: "angela",
        content: response.data.response,
        suggestions: response.data.suggestions,
        timestamp: new Date().toISOString(),
      }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "I'm having trouble connecting right now. Please try again in a moment.";
      setMessages(prev => [...prev, {
        role: "angela",
        content: detail,
        timestamp: new Date().toISOString(),
      }]);
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  const Icon = config.icon;
  const stageName = stage ? stage.charAt(0).toUpperCase() + stage.slice(1) : null;

  return (
    <div
      className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-[380px] bg-[#0d1318] border border-white/[0.08] rounded-t-2xl sm:rounded-2xl flex flex-col z-[100] overflow-hidden shadow-2xl"
      style={{ height: '520px' }}
      data-testid="angela-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2A9D9D] to-[#7B68EE] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Ask Angela</h3>
            <div className="flex items-center gap-1.5">
              <Icon className="w-3 h-3 text-[#2A9D9D]" />
              <p className="text-[#2A9D9D] text-[10px] font-bold uppercase tracking-wider" data-testid="angela-context-label">
                {page === "project" && stageName ? `${stageName} Stage` : contextKey.charAt(0).toUpperCase() + contextKey.slice(1)}
              </p>
            </div>
          </div>
        </div>
        <button onClick={onToggle} className="p-2 text-slate-500 hover:text-white transition-colors rounded-lg hover:bg-white/[0.05]">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Learned patterns (rung 3) — pre-mined, dismissible */}
        {insights.length > 0 && (
          <div className="space-y-2" data-testid="angela-insights-panel">
            <div className="flex items-center gap-1.5 px-1">
              <Lightbulb className="w-3.5 h-3.5 text-[#E0B341]" />
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#E0B341]">
                What Angela's learned about your business
              </p>
            </div>
            {insights.slice(0, 3).map((ins) => (
              <div
                key={ins.id}
                data-testid={`angela-insight-card-${ins.id}`}
                className="relative bg-[#151c23] border border-[#E0B341]/20 rounded-xl p-3 pr-8"
              >
                <button
                  onClick={() => dismissInsight(ins.id)}
                  data-testid={`angela-insight-dismiss-${ins.id}`}
                  className="absolute top-2 right-2 p-1 text-slate-500 hover:text-white rounded-md hover:bg-white/[0.06] transition-colors"
                  aria-label="Dismiss insight"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <p className="text-slate-100 text-xs font-semibold leading-snug">{ins.title}</p>
                <p className="text-slate-400 text-[11px] leading-relaxed mt-1 line-clamp-3">{ins.summary}</p>
              </div>
            ))}
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx}>
            {msg.role === "angela" ? (
              <div className="space-y-3">
                <div className="bg-[#151c23] border border-white/[0.06] rounded-2xl p-4">
                  <div className="angela-md text-slate-200 text-sm leading-relaxed" data-testid="angela-message-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </div>
                </div>

                {/* Context-aware quick actions */}
                {(msg.showActions || msg.suggestions) && (
                  <div className="flex flex-wrap gap-2" data-testid="angela-quick-actions">
                    {(msg.suggestions || config.actions.map(a => a.label)).slice(0, 3).map((item, i) => {
                      const actionDef = !msg.suggestions && config.actions[i];
                      const hasApiAction = actionDef?.apiAction;
                      return (
                        <button
                          key={i}
                          onClick={() => hasApiAction ? handleApiAction(actionDef.apiAction) : sendMessage(actionDef?.action || item)}
                          data-testid={`angela-action-${i}`}
                          className="px-3 py-2 rounded-lg bg-[#151c23] border border-white/[0.06] text-slate-300 text-xs hover:bg-[#7B68EE]/10 hover:border-[#7B68EE]/20 hover:text-white transition-colors"
                        >
                          {typeof item === 'string' ? item : item}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-end">
                <div className="bg-[#2A9D9D] text-white rounded-2xl rounded-br-sm px-4 py-3 max-w-[85%]">
                  <p className="text-sm">{msg.content}</p>
                </div>
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="bg-[#151c23] border border-white/[0.06] rounded-2xl p-4 w-20">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-[#2A9D9D]/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-[#2A9D9D]/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-[#2A9D9D]/60 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-white/[0.06]">
        <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Angela anything..."
            data-testid="angela-chat-input"
            className="flex-1 bg-transparent border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-[#2A9D9D]/50"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            data-testid="angela-send-btn"
            className="p-2.5 bg-[#2A9D9D] hover:bg-[#238585] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
}
